import Decimal from "decimal.js";
import { DensityUnits, UnitType } from "encamp-prisma/generated/browser";
import { createUnit, unit as math_unit, Unit } from "mathjs";

import { StateAbbreviation } from "../constants/states";
import {
  DensityUnit,
  getAmountCode,
  TIER_II_DENSITY_UNIT,
  TIER_II_UNIT,
} from "../constants/storageAmounts";
import { TierIIFacilityProduct } from "../generated-graphql";
import { hasValue } from "../utils/hasValue";
import { CouldNotDetermineUnit, MissingDensity, UnsupportedUnitConversion } from "./errors";

// This is to match the precision of the postgres `double precision` type
const DECIMAL_PRECISION = 15;

export const UnitTypeClassification: { [key in UnitType]: "Mass" | "Volume" } = {
  [UnitType.POUNDS]: "Mass",
  [UnitType.GALLONS]: "Volume",
  [UnitType.CUBIC_FEET]: "Volume",
  [UnitType.TONS]: "Mass",
  [UnitType.KILOGRAMS]: "Mass",
  [UnitType.METRIC_TONS]: "Mass",
  [UnitType.LITERS]: "Volume",
  [UnitType.BARRELS]: "Volume",
};

export class DensityRequiredError extends Error {
  constructor() {
    super("Conversions between mass and volume require a density.");
    this.name = "DensityRequiredError";
  }
}

export class UnsupportedConversionError extends Error {
  constructor(fromUnits: string, toUnits: string) {
    super(`Conversions from ${fromUnits} to ${toUnits} are currently not supported.`);
    this.name = "UnsupportedConversionError";
  }
}

createUnit(`${TIER_II_UNIT.POUNDS}`, "1 lb");
/**
 * Note: This is not for 55 gallon drums. It is a reference to the oil industry
 * standard of 42 gallons.
 */
createUnit(`${TIER_II_UNIT.BARRELS}`, "42 gallon");

/**
 * Convert pounds → kilograms. Thin wrapper around `convertFromUnitsToUnits` so
 * call sites can avoid hand-rolling the 0.45359237 conversion factor.
 */
export function poundsToKilograms(pounds: number): number {
  return convertFromUnitsToUnits(pounds, TIER_II_UNIT.POUNDS, TIER_II_UNIT.KILOGRAMS);
}

/**
 * Convert kilograms → pounds. Thin wrapper around `convertFromUnitsToUnits` so
 * call sites can avoid hand-rolling the 2.20462 conversion factor.
 */
export function kilogramsToPounds(kilograms: number): number {
  return convertFromUnitsToUnits(kilograms, TIER_II_UNIT.KILOGRAMS, TIER_II_UNIT.POUNDS);
}

export function convertFromUnitsToUnits(
  amount: number,
  fromUnits: TIER_II_UNIT | DensityUnit,
  toUnits: TIER_II_UNIT | DensityUnit,
  density?: number | null | undefined,
  densityUnits?: DensityUnit
): number {
  const fromBaseUnit = math_unit(mathSafeUnit(fromUnits));
  const toBaseUnit = math_unit(mathSafeUnit(toUnits));
  // if converting from similar types of units (mass to mass, volume to volume), just do the conversion
  if (fromBaseUnit.equalBase(toBaseUnit)) {
    return math_unit(amount, mathSafeUnit(fromUnits)).to(mathSafeUnit(toUnits)).toNumber();
  } else if (isConvertBetweenMassAndVolume(fromBaseUnit, toBaseUnit)) {
    if (!density || !densityUnits) {
      throw new DensityRequiredError();
    }
    let [numerator, denominator] = densityUnits.split("/");
    if (
      // If the density needs to be inverted...
      math_unit(mathSafeUnit(numerator)).units[0].unit.base.key !==
      toBaseUnit.units[0].unit.base.key
    ) {
      [denominator, numerator] = densityUnits.split("/");
      density = Number(Decimal.div(1, density).toPrecision(DECIMAL_PRECISION));
    }
    const densityWithCorrectNumerator: number = math_unit(density, mathSafeUnit(numerator))
      .to(mathSafeUnit(toUnits))
      .toNumber();

    // mathjs doesn't support inverse units and that's why there are a couple of ugly inverses here.
    const unitConversionFactor = Decimal.div(
      1,
      math_unit(1, mathSafeUnit(denominator)).to(mathSafeUnit(fromUnits)).toNumber()
    );
    return Number(
      Decimal.mul(
        Decimal.mul(amount, densityWithCorrectNumerator),
        unitConversionFactor
      ).toPrecision(DECIMAL_PRECISION)
    );
  } else {
    throw new UnsupportedConversionError(fromUnits, toUnits);
  }
}

export function canConvertWithoutDensity(fromUnit: UnitType, toUnit: UnitType) {
  // We need density for barrels
  if (fromUnit === UnitType.BARRELS && toUnit !== UnitType.BARRELS) {
    return false;
  }

  const fromTierIIUnit = convertUnitEnum(fromUnit);
  const toTierIIUnit = convertUnitEnum(toUnit);

  if (!fromTierIIUnit || !toTierIIUnit) {
    return false;
  }

  const baseFromUnit = math_unit(mathSafeUnit(fromTierIIUnit));
  const baseToUnit = math_unit(mathSafeUnit(toTierIIUnit));

  const areSameUnit = baseFromUnit.equalBase(baseToUnit);
  const isInvalidConvert = isConvertBetweenMassAndVolume(baseFromUnit, baseToUnit);
  return areSameUnit || !isInvalidConvert;
}

/**
 * Determines if a unit is acceptable given a list of allowed units and whether density is available.
 * If density is available, any unit is acceptable (can be converted).
 * Otherwise, checks if the unit can be converted to any of the allowed units without density.
 * @param unit - The unit to check
 * @param allowedUnits - Array of allowed units
 * @param hasDensity - Whether density information is available
 * @returns true if the unit is acceptable, false otherwise
 */
export function hasAcceptableUnit(
  unit: UnitType,
  allowedUnits: UnitType[],
  hasDensity: boolean
): boolean {
  if (hasDensity) {
    return true;
  }

  return allowedUnits.some((au) => canConvertWithoutDensity(unit, au));
}

const SPECIFIC_GRAVITY_OF_WATER = 8.34;
export function convertSpecificGravityToPoundsPerGallonDensity(
  specificGravityLow: number,
  specificGravityHigh?: number | null | undefined
) {
  const specificGravity =
    specificGravityHigh && !isNaN(specificGravityHigh) ? specificGravityHigh : specificGravityLow;
  return specificGravity * SPECIFIC_GRAVITY_OF_WATER;
}

export function convertStringDensityUnitToEnumValue(
  unit?: string | null
): TIER_II_DENSITY_UNIT | undefined {
  if (!unit) {
    return undefined;
  }

  const lowerUnit = unit.toLowerCase() as TIER_II_DENSITY_UNIT;

  if (Object.values(TIER_II_DENSITY_UNIT).includes(lowerUnit)) {
    return lowerUnit;
  }

  return undefined;
}

export function convertStringUnitToEnumValue(unit?: string): TIER_II_UNIT {
  const lowerUnit = unit?.toLowerCase() as TIER_II_UNIT;

  if (Object.values(TIER_II_UNIT).includes(lowerUnit)) {
    return lowerUnit;
  }

  return TIER_II_UNIT.POUNDS;
}

function isConvertBetweenMassAndVolume(fromUnit: Unit, toUnit: Unit) {
  // math.js will break up 'compound' units into their individual units. mass & volume
  // are not compound measurement types, so we should ensure that there are only 1 base unit
  // in each of the incoming units
  if (fromUnit.units.length !== 1 || toUnit.units.length !== 1) {
    return false;
  } else if (
    // unit.base.key is the category of unit represented (length, mass, volume, ect). We
    // want to make sure that both of the units are either MASS or VOLUME
    ![fromUnit.units[0].unit.base.key, toUnit.units[0].unit.base.key].every((x) =>
      ["VOLUME", "MASS"].includes(x)
    )
  ) {
    return false;
  }

  return true;
}

/* Our units don't coincide with the mathjs library's units & they don't support spaces in a unit
 * so we need to do this string munging. Ideally we could default to their units but this is fine.
 */
function mathSafeUnit(value: string): string {
  return value
    .replace("cubic feet", "cuft")
    .replace("cubic foot", "cuft")
    .replace("metric tons", "tonne");
}

/** @deprecated Stop converting your amounts this late in the game */
export function convertFacilityProductAmountsToUnit(
  facilityProduct: TierIIFacilityProduct,
  unit: TIER_II_UNIT,
  state?: StateAbbreviation,
  shouldConvertStorageLocations: boolean = true
): TierIIFacilityProduct {
  /*
   * For now, we need to make sure our facility product is in a unit we recognize
   * because all of our facility products currently store units as a string ;-(
   */
  const fromUnit: TIER_II_UNIT =
    (facilityProduct.unit?.toLowerCase() as TIER_II_UNIT) || TIER_II_UNIT.POUNDS;

  if (!Object.values(TIER_II_UNIT).includes(fromUnit as TIER_II_UNIT)) {
    throw new Error(`Unexpected from unit for conversion: ${fromUnit}`);
  }

  const { product } = facilityProduct;
  if (!product) {
    throw Error(`FacilityProduct has no product, cannot convert units. id: ${facilityProduct.id}`);
  }
  const facilityProductCopy = {
    ...facilityProduct,
    unit,
  };

  if (fromUnit !== unit) {
    facilityProductCopy.maxAmount = convertFromUnitsToUnits(
      facilityProductCopy.maxAmount ?? 0,
      fromUnit,
      unit,
      product.density,
      product.densityUnits as DensityUnit
    );

    facilityProductCopy.averageAmount = convertFromUnitsToUnits(
      facilityProductCopy.averageAmount ?? 0,
      fromUnit,
      unit,
      product.density,
      product.densityUnits as DensityUnit
    );

    if (
      facilityProductCopy.maxAmountLargestContainer !== null &&
      facilityProductCopy.maxAmountLargestContainer !== undefined
    ) {
      facilityProductCopy.maxAmountLargestContainer = convertFromUnitsToUnits(
        facilityProductCopy.maxAmountLargestContainer,
        fromUnit,
        unit,
        product.density,
        product.densityUnits as DensityUnit
      );
    }

    if (unit === TIER_II_UNIT.POUNDS && state) {
      const { averageAmount, maxAmount } = facilityProductCopy;

      facilityProductCopy.averageAmountCode = parseInt(
        getAmountCode(averageAmount.toString(), state)
      );

      facilityProductCopy.maxAmountCode = parseInt(getAmountCode(maxAmount.toString(), state));
    }
  }

  if (shouldConvertStorageLocations && facilityProductCopy.locations) {
    facilityProductCopy.locations = facilityProductCopy.locations
      .filter(hasValue)
      .map((location) => ({
        ...location,
        amount: location.amount
          ? convertFromUnitsToUnits(
              location.amount,
              fromUnit,
              unit,
              product.density,
              product.densityUnits as DensityUnit
            )
          : location.amount,
        unit: unit,
      }));
  }

  return facilityProductCopy;
}

export const convertToUnitType = (unit: TIER_II_UNIT | undefined): UnitType | null | undefined => {
  switch (unit) {
    case TIER_II_UNIT.POUNDS:
      return UnitType.POUNDS;
    case TIER_II_UNIT.BARRELS:
      return UnitType.BARRELS;
    case TIER_II_UNIT.CUBIC_FEET:
      return UnitType.CUBIC_FEET;
    case TIER_II_UNIT.TONS:
      return UnitType.TONS;
    case TIER_II_UNIT.METRIC_TONS:
      return UnitType.METRIC_TONS;
    case TIER_II_UNIT.LITERS:
      return UnitType.LITERS;
    case TIER_II_UNIT.GALLONS:
      return UnitType.GALLONS;
    case TIER_II_UNIT.KILOGRAMS:
      return UnitType.KILOGRAMS;
  }
};

/**
 * This function converts the unit from the UnitType enum to the TIER_II_UNIT enum.
 * @param {UnitType | null | undefined} unit - The unit to be converted.
 * @returns {TIER_II_UNIT | undefined} The converted unit.
 */
const convertUnitEnum = (unit: UnitType | null | undefined): TIER_II_UNIT | undefined => {
  switch (unit) {
    case UnitType.POUNDS:
      return TIER_II_UNIT.POUNDS;
    case UnitType.BARRELS:
      return TIER_II_UNIT.BARRELS;
    case UnitType.CUBIC_FEET:
      return TIER_II_UNIT.CUBIC_FEET;
    case UnitType.TONS:
      return TIER_II_UNIT.TONS;
    case UnitType.METRIC_TONS:
      return TIER_II_UNIT.METRIC_TONS;
    case UnitType.LITERS:
      return TIER_II_UNIT.LITERS;
    case UnitType.GALLONS:
      return TIER_II_UNIT.GALLONS;
    case UnitType.KILOGRAMS:
      return TIER_II_UNIT.KILOGRAMS;
  }
};

/**
 * This function converts the density unit from the DensityUnits enum to the TIER_II_DENSITY_UNIT enum.
 * @param {DensityUnits | null | undefined} unit - The density unit to be converted.
 * @returns {DensityUnit | undefined} The converted density unit.
 */
const convertDensityUnitEnum = (unit: DensityUnits | null | undefined): DensityUnit | undefined => {
  switch (unit) {
    case DensityUnits.POUNDS_PER_CUBIC_FOOT:
      return TIER_II_DENSITY_UNIT.POUNDS_PER_CUBIC_FOOT;
    case DensityUnits.POUNDS_PER_GALLON:
      return TIER_II_DENSITY_UNIT.POUNDS_PER_GALLON;
    case DensityUnits.POUNDS_PER_LITER:
      return TIER_II_DENSITY_UNIT.POUNDS_PER_LITERS;
    case DensityUnits.POUNDS_PER_BARREL:
      return TIER_II_DENSITY_UNIT.POUNDS_PER_BARRELS;

    case DensityUnits.KILOGRAMS_PER_GALLON:
      return TIER_II_DENSITY_UNIT.KILOGRAMS_PER_GALLON;
    case DensityUnits.KILOGRAMS_PER_LITER:
      return TIER_II_DENSITY_UNIT.KILOGRAMS_PER_LITERS;
    case DensityUnits.KILOGRAMS_PER_CUBIC_FOOT:
      return TIER_II_DENSITY_UNIT.KILOGRAMS_PER_CUBIC_FOOT;
    case DensityUnits.KILOGRAMS_PER_BARREL:
      return TIER_II_DENSITY_UNIT.KILOGRAMS_PER_BARRELS;

    case DensityUnits.TONS_PER_GALLON:
      return TIER_II_DENSITY_UNIT.TONS_PER_GALLON;
    case DensityUnits.TONS_PER_LITER:
      return TIER_II_DENSITY_UNIT.TONS_PER_LITERS;
    case DensityUnits.TONS_PER_CUBIC_FOOT:
      return TIER_II_DENSITY_UNIT.TONS_PER_CUBIC_FOOT;
    case DensityUnits.TONS_PER_BARREL:
      return TIER_II_DENSITY_UNIT.TONS_PER_BARRELS;

    case DensityUnits.METRIC_TONS_PER_GALLON:
      return TIER_II_DENSITY_UNIT.METRIC_TONS_PER_GALLON;
    case DensityUnits.METRIC_TONS_PER_LITER:
      return TIER_II_DENSITY_UNIT.METRIC_TONS_PER_LITERS;
    case DensityUnits.METRIC_TONS_PER_CUBIC_FOOT:
      return TIER_II_DENSITY_UNIT.METRIC_TONS_PER_CUBIC_FOOT;
    case DensityUnits.METRIC_TONS_PER_BARREL:
      return TIER_II_DENSITY_UNIT.METRIC_TONS_PER_BARRELS;

    default:
      return undefined;
  }
};

/**
 * This function converts the amount from one unit to another.
 * It throws an error if the fromUnit or toUnit is not provided.
 * @param {number | null | undefined} amount - The amount to be converted.
 * @param {UnitType | null | undefined} from - The unit from which the amount is to be converted.
 * @param {UnitType} to - The unit to which the amount is to be converted.
 * @param {number | null | undefined} density - The density of the substance.
 * @param {DensityUnits | null | undefined} densityUnit - The unit of the density.
 * @returns {number | null} The converted amount.
 */
export const convert = (
  amount: number | null | undefined,
  from: UnitType | null | undefined,
  to: UnitType,
  density: number | null | undefined,
  densityUnit: DensityUnits | null | undefined
): number | null => {
  const fromUnit = convertUnitEnum(from);
  const toUnit = convertUnitEnum(to);

  if (!fromUnit) {
    throw new CouldNotDetermineUnit("from");
  }
  if (!toUnit) {
    throw new CouldNotDetermineUnit("to");
  }

  // short circuit if we don't need to convert
  if (fromUnit === toUnit || !amount) return amount ?? null;

  const densityUnits = convertDensityUnitEnum(densityUnit);
  try {
    const convertedAmount = convertFromUnitsToUnits(
      amount,
      fromUnit,
      toUnit,
      density,
      densityUnits
    );
    return convertedAmount;
  } catch (error) {
    if (error instanceof DensityRequiredError) {
      throw new MissingDensity(fromUnit, toUnit);
    }
    if (error instanceof UnsupportedConversionError) {
      throw new UnsupportedUnitConversion(fromUnit, toUnit);
    }
    throw error;
  }
};
